import React, { useState, useContext, useRef, useCallback } from 'react';
import './styles.scss';
import api from '../../Services/api';
import { MARCAR_PONTO, APROVAR_PONTO, REPROVAR_PONTO } from '../../Services/Endpoints';
import { showToast } from '../../Functions';
import MainContext from '../../Contexts/MainContext';
import { GetAllColaboradores, GetAllFeriados, GetAllHorarios } from '../../Services/ApiCalls';
import { IoMdClose, IoMdTime, IoMdAlert, IoMdTrash, IoMdStar, IoMdCafe, IoMdCheckmark, IoMdCloseCircle } from 'react-icons/io';
import { FaBirthdayCake } from 'react-icons/fa';

interface ModalDetalhesDiaProps {
    date: Date;
    pontos: any[];
    onClose: () => void;
    onSuccess: () => void;
    readOnly?: boolean;
}

// ── Type Buttons ──────────────────────────────────────────────────────────────
const TIPOS_MAIN = [
    { key: 'Entrada' },
    { key: 'Início Intervalo' },
    { key: 'Término Intervalo' },
    { key: 'Saída' },
];

// ── Main Component ────────────────────────────────────────────────────────────
const ModalDetalhesDia: React.FC<ModalDetalhesDiaProps> = ({ date, pontos, onClose, onSuccess, readOnly = false }) => {
    const { currentLoggedUserId, token } = useContext(MainContext);

    const { dataAllFeriados } = GetAllFeriados(token);
    const { dataAllColaboradores } = GetAllColaboradores(token);
    const { dataAllHorarios } = GetAllHorarios(token);

    const nowH = String(new Date().getHours()).padStart(2, '0');
    const nowM = String(new Date().getMinutes()).padStart(2, '0');

    const [selectedType, setSelectedType] = useState('Entrada');
    const [isExtra, setIsExtra] = useState(false);
    const [finalTime, setFinalTime] = useState(`${nowH}:${nowM}`);
    const [justification, setJustification] = useState('');
    const [isSaving,  setIsSaving]  = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

    const dateFormatted = date.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
    const dateYYYYMMDD = date.toISOString().split('T')[0];
    const monthDay = dateYYYYMMDD.substring(5); // "MM-DD"
    
    const dayOfWeek     = date.toLocaleDateString("pt-BR", { weekday: 'long' });
    const dayPontos     = pontos.filter(p => p.data === dateFormatted);

    const feriadosDoDia = dataAllFeriados?.filter((f: any) => {
        if (!f.data) return false;
        if (f.recorrente) return f.data.substring(5) === monthDay;
        return f.data === dateYYYYMMDD;
    });

    const aniversariantesDoDia = dataAllColaboradores?.filter((c: any) => {
        if (!c.data_nascimento) return false;
        return c.data_nascimento.substring(5) === monthDay;
    });

    const currentUser = dataAllColaboradores?.find((c: any) => Number(c.id) === currentLoggedUserId);

    // Helpers de tempo
    const timeToMinutes = (timeStr: string) => {
        if (!timeStr) return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const minutesToTime = (mins: number) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    // Cálculos de HE e Normais
    const isHoliday = feriadosDoDia && feriadosDoDia.length > 0;
    const aliquota = isHoliday ? "100%" : "50%";
    
    let totalWorkedMinutes = 0;
    let entries: number[] = [];
    let exits: number[] = [];
    
    dayPontos.forEach(p => {
        const t = p.tipo?.toLowerCase() || '';
        if (t.includes('entrada') || t.includes('término intervalo') || t.includes('termino intervalo')) {
            entries.push(timeToMinutes(p.horario));
        }
        if (t.includes('saída') || t.includes('saida') || t.includes('início intervalo') || t.includes('inicio intervalo')) {
            exits.push(timeToMinutes(p.horario));
        }
    });
    
    entries.sort((a, b) => a - b);
    exits.sort((a, b) => a - b);
    
    for (let i = 0; i < Math.min(entries.length, exits.length); i++) {
        totalWorkedMinutes += Math.max(0, exits[i] - entries[i]);
    }

    // Identificar a jornada contratual do dia
    let JORNADA_NORMAL_MINUTES = 480; // default 8 horas
    const colabIdOfThisDay = dayPontos.length > 0 ? dayPontos[0].colaboradorId : null;
    const colabObj = dataAllColaboradores?.find((c: any) => Number(c.id) === Number(colabIdOfThisDay));
    
    if (colabObj && dataAllHorarios) {
        const horario = dataAllHorarios.find((h: any) => h.id === colabObj.horarioId) || dataAllHorarios.find((h: any) => h.padrao);
        if (horario) {
            const daysArr = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
            const dayOfWeekStr = daysArr[date.getDay()];
            const diaRegra = horario.dias?.find((dRule: any) => dRule.diaDaSemana === dayOfWeekStr);
            if (diaRegra && diaRegra.ativo) {
                const inM = timeToMinutes(diaRegra.entrada);
                const outM = timeToMinutes(diaRegra.saida);
                const breakIn = timeToMinutes(diaRegra.inicioIntervalo);
                const breakOut = timeToMinutes(diaRegra.fimIntervalo);
                
                const workedExpected = Math.max(0, (outM - inM) - Math.max(0, breakOut - breakIn));
                if (workedExpected > 0) {
                    JORNADA_NORMAL_MINUTES = workedExpected;
                }
            } else if (diaRegra && !diaRegra.ativo) {
                JORNADA_NORMAL_MINUTES = 0; // Fim de semana ou dia de folga
            }
        }
    }

    let horasNormaisMin = 0;
    let horasExtrasMin = 0;

    if (isHoliday) {
        horasExtrasMin = totalWorkedMinutes;
    } else {
        horasNormaisMin = Math.min(totalWorkedMinutes, JORNADA_NORMAL_MINUTES);
        horasExtrasMin = Math.max(0, totalWorkedMinutes - JORNADA_NORMAL_MINUTES);
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!finalTime) {
            showToast("ERROR", "Selecione o horário do ponto", {});
            return;
        }
        if (!justification.trim()) {
            showToast("ERROR", "Insira uma justificativa para o ajuste", {});
            return;
        }
        const finalTypeStr = isExtra ? `Extra - ${selectedType}` : selectedType;
        const payload = {
            manual: true,
            colaboradorId: currentLoggedUserId,
            data: dateFormatted,
            horario: finalTime,
            localizacao: "",
            tipo: finalTypeStr,
            observacao: justification,
            status: "PENDENTE",
            is_extra: isExtra,
            manual_adjustment: true,
        };
        setIsSaving(true);
        try {
            await api.post(MARCAR_PONTO, payload, { headers: { Authorization: token } });
            showToast("SUCCESS", "Solicitação de ajuste enviada com sucesso!", {});
            onSuccess();
        } catch {
            showToast("ERROR", "Erro ao enviar solicitação", {});
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteConfirm = async (id: number) => {
        setDeletingId(id);
        try {
            await api.delete(`${MARCAR_PONTO}/${id}`, { headers: { Authorization: token } });
            showToast("SUCCESS", "Registro excluído com sucesso!", {});
            setConfirmDeleteId(null);
            onSuccess();
        } catch {
            showToast("ERROR", "Erro ao excluir registro", {});
        } finally {
            setDeletingId(null);
        }
    };

    const handleApprove = async (ponto: any) => {
        try {
            const approverName = currentUser?.nome || 'Admin';
            await api.post(`${APROVAR_PONTO}`, { id: ponto.id, observacao: `${ponto.observacao || ''} [Aprovado por ${approverName}]` }, { headers: { Authorization: token } });
            showToast("SUCCESS", "Registro Aprovado!", {});
            onSuccess();
        } catch {
            showToast("ERROR", "Erro ao aprovar registro", {});
        }
    };

    const handleReject = async (ponto: any) => {
        try {
            const approverName = currentUser?.nome || 'Admin';
            await api.post(`${REPROVAR_PONTO}`, { id: ponto.id, observacao: `${ponto.observacao || ''} [Reprovado por ${approverName} - Contate o RH]` }, { headers: { Authorization: token } });
            showToast("SUCCESS", "Registro Reprovado!", {});
            onSuccess();
        } catch {
            showToast("ERROR", "Erro ao reprovar registro", {});
        }
    };

    const getStatusLabel = (p: any) => {
        const s = (p.status || '').toUpperCase();
        let text = 'Em Análise';
        let cls = 'pending';

        if (s === 'APROVADO') {
            cls = 'approved';
            text = 'Aprovado';
            if (p.observacao && p.observacao.includes('[Aprovado por')) {
                const match = p.observacao.match(/\[(Aprovado por [^\]]+)\]/);
                if (match) text = match[1];
            }
        } else if (s === 'REJEITADO') {
            cls = 'rejected';
            text = 'Reprovado';
            if (p.observacao && p.observacao.includes('[Reprovado por')) {
                const match = p.observacao.match(/\[(Reprovado por [^\]]+)\]/);
                if (match) text = match[1];
            }
        }
        return { text, cls };
    };

    return (
        <div className="modal-detalhes__overlay" onClick={onClose}>
            <div className="modal-detalhes__container" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-detalhes__header">
                    <div>
                        <h2>Registros do Dia</h2>
                        <p className="modal-detalhes__sub">
                            {dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1)}, {dateFormatted}
                        </p>
                    </div>
                    <button className="btn-close" onClick={onClose}><IoMdClose size={22} /></button>
                </div>

                <div className={`modal-detalhes__body ${readOnly ? 'read-only-mode' : ''}`}>
                    {/* Coluna Esquerda: Contexto + Registros */}
                    <div className="left-column">
                        {/* Headers de Contexto Dinâmico */}
                        <div className="dynamic-headers">
                            {feriadosDoDia && feriadosDoDia.map((f: any) => (
                                <div key={f.id} className="context-header feriado-header">
                                    <IoMdStar className="context-icon" />
                                    <div className="context-info">
                                        <span className="context-title">{f.nome}</span>
                                        <span className="context-subtitle">{f.tipo || "Feriado"}</span>
                                    </div>
                                </div>
                            ))}
                            {aniversariantesDoDia && aniversariantesDoDia.map((c: any) => (
                                <div key={c.id} className="context-header aniversario-header">
                                    <FaBirthdayCake className="context-icon" />
                                    <div className="context-info">
                                        <span className="context-title">Aniversário de {c.nome}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Registros */}
                        <div className="section-registros">
                            <h3>Registros Existentes ({dayPontos.length})</h3>
                            {dayPontos.length === 0 ? (
                                <div className="empty-state">
                                    <IoMdTime className="empty-icon" />
                                    <p>Nenhum ponto registrado para esta data.</p>
                                </div>
                            ) : (
                                <div className="pontos-list">
                                    {dayPontos.map((p, idx) => {
                                        const statusInfo   = getStatusLabel(p);
                                        const isConfirming = confirmDeleteId === (p.id || idx);
                                        const isDeleting   = deletingId    === (p.id || idx);
                                        const isPendente = p.status === "PENDENTE";
                                        
                                        // Limpar observação suja de logs do RH
                                        let cleanObs = p.observacao || '';
                                        cleanObs = cleanObs.replace(/\[Aprovado por [^\]]+\]/, '').replace(/\[Reprovado por [^\]]+\]/, '').trim();

                                        return (
                                            <div key={p.id || idx} className={`ponto-item ${isConfirming ? 'confirming-delete' : ''}`}>
                                                <div className="ponto-info">
                                                    <span className="ponto-time">{p.horario}</span>
                                                    <span className="ponto-type">{p.tipo || "Entrada"}</span>
                                                    {p.is_extra && <span className="ponto-extra-badge">Extra</span>}
                                                </div>
                                                <div className="ponto-meta">
                                                    <span className="ponto-origin">{p.manual ? "Ajuste Manual" : "Automático"}</span>
                                                    <span className={`status-badge ${statusInfo.cls}`}>{statusInfo.text}</span>
                                                </div>
                                                {cleanObs && (
                                                    <div className="ponto-obs">
                                                        <IoMdAlert className="obs-icon" />
                                                        <span><strong>Obs:</strong> {cleanObs}</span>
                                                    </div>
                                                )}
                                                
                                                {/* Auditoria Silenciosa - Só aparece se readOnly for true e status for PENDENTE */}
                                                {readOnly && isPendente && (
                                                    <div className="ponto-audit-actions">
                                                        <button className="btn-audit-approve" onClick={() => handleApprove(p)} title="Aprovar">
                                                            <IoMdCheckmark /> Aprovar
                                                        </button>
                                                        <button className="btn-audit-reject" onClick={() => handleReject(p)} title="Reprovar">
                                                            <IoMdCloseCircle /> Reprovar
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Ações de Exclusão (Admin) */}
                                                {!isConfirming ? (
                                                    <div className="ponto-actions">
                                                        {readOnly && <button className="btn-delete" onClick={() => setConfirmDeleteId(p.id || idx)} title="Excluir registro"><IoMdTrash /></button>}
                                                    </div>
                                                ) : (
                                                    <div className="ponto-confirm-delete">
                                                        <span className="confirm-text">Excluir?</span>
                                                        <div className="confirm-btns">
                                                            <button className="btn-confirm-yes" onClick={() => handleDeleteConfirm(p.id || idx)} disabled={isDeleting}>Sim</button>
                                                            <button className="btn-confirm-no" onClick={() => setConfirmDeleteId(null)} disabled={isDeleting}>Não</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Financial Footer (Auditoria) */}
                        {readOnly && (
                            <div className="section-financeira">
                                <div className="fin-card">
                                    <span className="fin-label">Horas Normais</span>
                                    <span className="fin-value">{minutesToTime(horasNormaisMin)}</span>
                                </div>
                                <div className="fin-card extra">
                                    <span className="fin-label">Horas Extras ({aliquota})</span>
                                    <span className="fin-value">{minutesToTime(horasExtrasMin)}</span>
                                </div>
                                <div className="fin-card total">
                                    <span className="fin-label">Total Realizado</span>
                                    <span className="fin-value">{minutesToTime(totalWorkedMinutes)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Coluna Direita: Formulário */}
                    {!readOnly && (
                        <div className="right-column">
                            <div className="section-solicitacao">
                            <h3>Solicitar Ajuste / Completar Dia</h3>
                            <form onSubmit={handleSave}>
                                {/* Tipo de Registro — botões modernos */}
                                <div className="form-group">
                                    <label>Tipo de Registro</label>
                                    <div className="tipo-grid">
                                        {TIPOS_MAIN.map(t => (
                                            <button
                                                key={t.key}
                                                type="button"
                                                className={`tipo-btn ${selectedType === t.key ? 'active' : ''}`}
                                                onClick={() => setSelectedType(t.key)}
                                            >
                                                {t.key}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            className={`tipo-btn extra-toggle ${isExtra ? 'active' : ''}`}
                                            onClick={() => setIsExtra(!isExtra)}
                                        >
                                            Hora Extra
                                        </button>
                                    </div>
                                </div>

                                {/* Seletor de hora */}
                                <div className="form-group">
                                    <label>Horário do Registro</label>
                                    <div className="elegant-time-container">
                                        <input
                                            type="time"
                                            className="elegant-time-input"
                                            value={finalTime}
                                            onChange={e => setFinalTime(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Justificativa */}
                                <div className="form-group">
                                    <label>Justificativa / Motivo</label>
                                    <textarea
                                        placeholder="Explique o motivo deste ajuste ou por que faltou o registro..."
                                        value={justification}
                                        onChange={e => setJustification(e.target.value)}
                                        rows={3}
                                        required
                                    />
                                </div>

                                <button type="submit" className="btn-submit" disabled={isSaving}>
                                    {isSaving ? "Enviando..." : "Enviar Solicitação"}
                                </button>
                            </form>
                        </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModalDetalhesDia;
